import React from "react";
import PropTypes from "prop-types";
import Button from "../custom-widgets/button";
import Icon from "../custom-widgets/icon";
import TextSuggestion from "./text-suggestion";

import styles from "./search-bar.module.scss";

/**
 * MegaMenu Result component
 *
 * This component is very similar to the regular results-container one, however, this one is specifically for the MM searches.
 * But it is simpler, having no onFocus.
 *
 * @param {Object} props - The component props
 * @param {Object} props.suggestion - The suggestion object
 *  @param {string} props.suggestion.title - The title of the suggestion (Page title)
 *  @param {string} props.suggestion.url - The URL of the suggestion (Page URL)
 *  @param {string} props.suggestion.id - The ID of the suggestion (Page ID)
 * @param {string} props.query - The current search query
 * @param {string} props.type - The type of the search bar (default: "mobile")
 * @param {number} props.index - The index of the suggestion in the list
 * @param {boolean} props.isFirst - Flag indicating if this is the first suggestion
 * @param {boolean} props.isLast - Flag indicating if this is the last suggestion
 * @param {Function} props.onPopulateQueryButtonClick - Function to handle the magnifying glass button to the left that populates the search bar with the suggestion.title. Takes the suggestion title as an argument.
 * @param {Function} props.onResultSearchButtonClick - Function to handle result search button click, takes the suggestion text, suggestion ID, and index as arguments.
 *
 * @returns {JSX.Element} The rendered Result component
 */
const Result = ({
  suggestion = { title: "", url: "", id: "" },
  query = "",
  type = "mobile",
  index = 0,
  isFirst = true,
  isLast = false,
  onPopulateQueryButtonClick = (title) => {},
  onResultSearchButtonClick = (suggestionText, suggestionId, index) => {}
}) => {
  const title = suggestion.title?.replaceAll(" | WaFd Bank", "");
  const suggestionText = title ?? suggestion.url;
  const getMargins = () => {
    if (type === "mobile") {
      if (isFirst) return "ml-4 mr-4 mt-2";
      if (isLast) return "ml-4 mr-4 mb-2";
      return "ml-4 mr-4 my-2";
    } else {
      if (isFirst) return "mt-2";
      return "";
    }
  };

  return (
    <div key={index} className={`d-flex flex-row ${getMargins()}`}>
      <Button
        id={`ssi-suggestion-icon-button-${index}`}
        type="button"
        onClick={() => onPopulateQueryButtonClick(title)}
        icon={{ name: "search", class: "fa-lg" }}
        class={`bg-white ${type === "mobile" ? "no-min-width w-auto pl-0" : styles.btnSuggestionExtraIcon}`}
        containerClass="w-auto"
      />
      <div className="d-flex flex-grow-1">
        <button
          className={`btn ${type === "mobile" ? "pl-3 mr-0" : ""} ${styles.btnSuggestion}`}
          id={`ssi-suggestion-button-${index}`}
          onClick={() => {
            onResultSearchButtonClick(suggestionText, suggestion.id, index);
          }}
        >
          <TextSuggestion text={suggestionText} query={query} />
          <Icon name="arrow-right" class={`fa-lg float-right ${styles.btnSuggestionIcon}`} />
        </button>
      </div>
    </div>
  );
};

/**
 * MegaMenu Results Container component
 *
 * @param {Object} props - The component props
 * @param {Array} props.hits - The list of hits (suggestions) to display
 * @param {string} props.query - The current search query
 * @param {string} props.type - The type of the search bar (default: "mobile")
 * @param {Function} props.onPopulateQueryButtonClick - Function to handle populate query button click, takes the suggestion title as an argument.
 * @param {Function} props.onResultSearchButtonClick - Function to handle result search button click, takes the suggestion text, suggestion ID, and index as arguments.
 *
 * @returns {JSX.Element} The rendered MegaMenuResultsContainer component
 */
const MegaMenuResultsContainer = ({
  hits = [],
  query = "",
  type = "mobile",
  onPopulateQueryButtonClick = (suggestionTitle) => {},
  onResultSearchButtonClick = (suggestionText, suggestionId, index) => {}
}) => {
  const hasQuery = query && query.length > 0;
  const hasHits = hits && hits.length > 0;

  return (
    <>
      {hasQuery &&
        hasHits &&
        hits.map((hit, index) => (
          <Result
            key={index}
            suggestion={hit}
            isFirst={index === 0}
            isLast={index === hits.length - 1}
            index={index}
            onPopulateQueryButtonClick={onPopulateQueryButtonClick}
            onResultSearchButtonClick={onResultSearchButtonClick}
            type={type}
            query={query}
          />
        ))}
      {hasQuery && !hasHits && (
        <div className="input-group mt-2 mx-3">
          <p id="ssi-no-results-found-message" className={`h6 ${type === "mobile" ? "mb-2" : "mb-0"}`}>
            No results found
          </p>
        </div>
      )}
    </>
  );
};

export default MegaMenuResultsContainer;

Result.propTypes = {
  suggestion: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    id: PropTypes.string
  }).isRequired,
  query: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isFirst: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
  onPopulateQueryButtonClick: PropTypes.func.isRequired,
  onResultSearchButtonClick: PropTypes.func.isRequired
};

MegaMenuResultsContainer.propTypes = {
  hits: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
      id: PropTypes.string
    })
  ).isRequired,
  query: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  onPopulateQueryButtonClick: PropTypes.func.isRequired,
  onResultSearchButtonClick: PropTypes.func.isRequired
};
